import { useTranslation } from 'react-i18next'
import { Info, X } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useWorkflowLifecycleStore } from '@/stores/workflow-lifecycle-store'

interface EditPublishedBannerProps {
  workflowId: string
  currentVersion: number
}

/**
 * Info banner rendered atop Fluxo / Config tabs when a workflow is published
 * (status === 'active'). Warns the user that editing will spawn a new draft.
 * Dismissal is session-only (stored in the lifecycle zustand store, not
 * persisted) per 03-UI-SPEC §Edit-published banner.
 */
export function EditPublishedBanner({
  workflowId,
  currentVersion,
}: EditPublishedBannerProps) {
  const { t } = useTranslation('workflows')
  const dismissed = useWorkflowLifecycleStore(
    (s) => s.editPublishedBannerDismissed[workflowId]
  )
  const dismiss = useWorkflowLifecycleStore(
    (s) => s.dismissEditPublishedBanner
  )

  if (dismissed) return null

  return (
    <Alert
      className="border-primary/30 bg-primary/5 mb-4"
      data-testid="edit-published-banner"
    >
      <Info className="h-4 w-4" />
      <AlertTitle>{t('lifecycle.editPublishedBanner.title')}</AlertTitle>
      <AlertDescription className="flex items-center justify-between gap-3">
        <span>
          {t('lifecycle.editPublishedBanner.body', { current: currentVersion })}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => dismiss(workflowId)}
          data-testid="edit-published-banner-dismiss"
        >
          <X className="h-4 w-4 mr-1" />
          {t('lifecycle.editPublishedBanner.dismiss')}
        </Button>
      </AlertDescription>
    </Alert>
  )
}
